import React, { memo, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { RadioButton } from 'primereact/radiobutton'

import { RootState } from '../../redux/store'
import { WidgetLayout } from '../../../lib/widget-layout'
import hasClaimForCompany from '../../../lib/hasClaimForCompany'
import Claims from '../../../lib/claims'
import {
    ChargePointUser,
    useCompanyUpdateRoleToUserMutation,
    useCompanyUpdateUserMutation,
} from '../../../common/redux/api/web-api'
import canChangePassword from '../../../lib/canChangePassword'
import useText from '../../../common/hooks/useText'
import { Input } from '../../../common/uikit/Input'
import { useToast } from '../../../common/uikit/ToastProvider'

type UserDetailsFormValues = {
    firstName: string
    lastName: string
    email: string
    mobile: string
    roleInCompany: string
    newPassword: string
    confirmNewPassword: string
}

const getFormValues = (user: ChargePointUser): UserDetailsFormValues => ({
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    mobile: user.mobile ?? '',
    roleInCompany: user.roleInCompany ?? '',
    newPassword: '',
    confirmNewPassword: '',
})

const userRoles = [
    { value: 'User', labelCode: 'lblUserTypeUser', testId: 'radio-user' },
    { value: 'Admin', labelCode: 'lblUserTypeAdmin', testId: 'radio-admin' },
    { value: 'AdminViewOnly', labelCode: 'lblUserTypeAdminViewOnly', testId: 'radio-aminView' },
] as const

export const UserDetails = memo(function UserDetails(): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const signedInUser = useSelector((state: RootState) => state.user)
    const user = useSelector((state: RootState) => state.selectedUser)
    const [openPasswordChange, setOpenPasswordChange] = useState(false)
    const canEditUser = hasClaimForCompany(signedInUser, signedInUser.selectedCompanyId, Claims.Admin)
    const canEditPassword =
        canEditUser && (canChangePassword(signedInUser, user) || Boolean(user.passwordChangeableForAdmin))
    const userFullName = [user.firstName, user.lastName].filter(Boolean).join(' ')

    const [updateUser, { isLoading: updatingUser }] = useCompanyUpdateUserMutation()
    const [updateRole, { isLoading: updatingRole }] = useCompanyUpdateRoleToUserMutation()

    const {
        control,
        getValues,
        handleSubmit,
        register,
        reset,
        unregister,
        formState: { errors, isSubmitted, isSubmitting },
    } = useForm<UserDetailsFormValues>({
        defaultValues: getFormValues(user),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        reset(getFormValues(user))
        setOpenPasswordChange(false)
    }, [reset, user])

    const setPasswordChangeVisible = (visible: boolean) => {
        setOpenPasswordChange(visible)

        if (!visible) {
            unregister(['newPassword', 'confirmNewPassword'])
        }
    }

    const toChargePointUser = (values: UserDetailsFormValues): ChargePointUser => ({
        ...user,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile || null,
        password: user.password,
        passwordChange: openPasswordChange ? { newPassword: values.newPassword } : null,
        roleInCompany: values.roleInCompany,
        userStatus: user.userStatus,
    })

    const saveExistingUser = async (chargePointUser: ChargePointUser) => {
        if (!chargePointUser.id) return

        await updateUser({ companyId: signedInUser.selectedCompanyId, chargePointUser }).unwrap()
        await updateRole({
            companyId: signedInUser.selectedCompanyId,
            userId: chargePointUser.id,
            role: {
                claim: chargePointUser.roleInCompany,
                companyId: signedInUser.selectedCompanyId,
                chargePointUserId: chargePointUser.id,
            },
        }).unwrap()

        toast.notifySuccess(text(openPasswordChange ? 'msgProfilePasswordUpdated' : 'msgUserUpdated'))
    }

    const save = async (values: UserDetailsFormValues) => {
        if (!canEditUser) return

        const chargePointUser = toChargePointUser(values)

        try {
            await saveExistingUser(chargePointUser)
        } catch (error) {
            toast.notifyError(text('msgUserNotSaved'))
            console.error(error)
        }
    }

    const isLoading = updatingRole || updatingUser
    const saveDisabled = !canEditUser || isLoading || isSubmitting || (isSubmitted && Object.keys(errors).length > 0)

    return (
        <WidgetLayout
            title={
                <header className="flex flex-column gap-1">
                    <h2 className="text-xl font-semibold m-0">{userFullName}</h2>
                    {user.email ? (
                        <span className="text-sm font-normal text-600" data-testid="user-details-header-email">
                            {user.email}
                        </span>
                    ) : null}
                </header>
            }
            data-testid="userDetails"
        >
            <form onSubmit={handleSubmit(save)} autoComplete="off" noValidate>
                <div className="formgrid grid">
                    <div className="field col-12 md:col-6" data-testid="firstName">
                        <Input
                            id="first-name-input"
                            label={text('lblUserDetailsFirstNameField')}
                            data-testid="firstName-input"
                            disabled={!canEditUser}
                            required
                            error={errors.firstName?.message}
                            {...register('firstName', { required: text('msgFirstNameRequired') })}
                        />
                    </div>

                    <div className="field col-12 md:col-6" data-testid="lastName">
                        <Input
                            id="last-name-input"
                            label={text('lblUserDetailsLastNameField')}
                            data-testid="lastName-input"
                            disabled={!canEditUser}
                            required
                            error={errors.lastName?.message}
                            {...register('lastName', { required: text('msgLastNameRequired') })}
                        />
                    </div>

                    <div className="field col-12 md:col-6" data-testid="email">
                        <Input
                            id="email-input"
                            type="email"
                            label={text('lblEmail')}
                            data-testid="email-input"
                            autoComplete="email"
                            disabled
                            required
                            error={errors.email?.message}
                            {...register('email', { required: text('msgEmailRequired') })}
                        />
                    </div>

                    <div className="field col-12 md:col-6" data-testid="mobile">
                        <Input
                            id="mobile-input"
                            type="tel"
                            label={text('lblMobile')}
                            data-testid="mobile-input"
                            disabled={!canEditUser}
                            {...register('mobile')}
                        />
                    </div>

                    <div className="field col-12 mb-3" role="group" aria-labelledby="user-role-label">
                        <span id="user-role-label" className="p-label block mb-2">
                            {text('lblRole')}
                        </span>
                        <Controller
                            name="roleInCompany"
                            control={control}
                            rules={{ required: text('msgRequiredShort') }}
                            render={({ field }) => (
                                <div className="flex flex-column gap-2">
                                    {userRoles.map((role) => (
                                        <div key={role.value} className="flex align-items-center gap-2">
                                            <RadioButton
                                                inputId={`user-role-${role.value}`}
                                                value={role.value}
                                                name={field.name}
                                                className="w-1rem h-1rem"
                                                checked={field.value === role.value}
                                                disabled={!canEditUser}
                                                onBlur={field.onBlur}
                                                onChange={(event) => field.onChange(event.value)}
                                                data-testid={role.testId}
                                                pt={{
                                                    box: { className: 'w-1rem h-1rem' },
                                                    icon: { style: { width: '0.5rem', height: '0.5rem' } },
                                                }}
                                            />
                                            <label htmlFor={`user-role-${role.value}`}>{text(role.labelCode)}</label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                        {errors.roleInCompany?.message ? (
                            <Message severity="error" text={errors.roleInCompany.message} className="mt-2" />
                        ) : null}
                    </div>

                    {canEditPassword ? (
                        <div className="col-12">
                            <Button
                                label={text('lblChangingPassword')}
                                icon={openPasswordChange ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                                type="button"
                                link
                                size="small"
                                className="p-0"
                                aria-expanded={openPasswordChange}
                                aria-controls="change-password-fields"
                                onClick={() => setPasswordChangeVisible(!openPasswordChange)}
                                data-testid="change-password"
                            />

                            {openPasswordChange ? (
                                <div id="change-password-fields" className="grid formgrid mt-3">
                                    <div className="field col-12 md:col-6">
                                        <Input
                                            id="new-password-input"
                                            type="password"
                                            label={text('lblNewPasswordField')}
                                            data-testid="newPassword-input"
                                            required
                                            subtitle={text('msgMinimumPasswordLength')}
                                            error={errors.newPassword?.message}
                                            {...register('newPassword', {
                                                required: text('msgPasswordRequired'),
                                                minLength: { value: 8, message: text('msgPasswordToShort') },
                                            })}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <Input
                                            id="confirm-new-password-input"
                                            type="password"
                                            label={text('lblUserDetailsConfirmPasswordField')}
                                            data-testid="passcordCompare-input"
                                            required
                                            error={errors.confirmNewPassword?.message}
                                            {...register('confirmNewPassword', {
                                                required: text('msgPasswordDoNotMatch'),
                                                deps: 'newPassword',
                                                validate: (value) =>
                                                    value === getValues('newPassword') || text('msgPasswordDoNotMatch'),
                                            })}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className="flex justify-content-end mt-4">
                    <Button
                        label={text('btnSave')}
                        icon="pi pi-save"
                        disabled={saveDisabled}
                        loading={isLoading || isSubmitting}
                        type="submit"
                        data-testid="btnSave"
                    />
                </div>
            </form>
        </WidgetLayout>
    )
})
